import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import axios from 'axios';

import { FaUserClock, FaUserPlus } from 'react-icons/fa';

import TeamsList from './components/TeamsList';
import TeamFormModal from './components/TeamFormModal';
import MemberCard from './components/MemberCard';
import MemberDetailsModal from './components/MemberDetailsModal';
import InterviewSetupModal from '../../components/Teams/interview/InterviewSetupModal';
import AddMemberOptionsModal from './components/AddMemberOptionsModal';
import InterviewVideoCall from '../../components/Teams/interview/InterviewVideoCall';
import MilitarySessionVideoCall from '../../components/Teams/military/MilitarySessionVideoCall';
import DetailedAnalyticsModal from '../../components/Teams/interview/DetailedAnalyticsModal';
import EndInterviewModal from '../../components/Teams/interview/EndInterviewModal';
import PendingCandidatesModal from '../../components/Teams/interview/PendingCandidatesModal';
import MilitaryEndReviewModal from '../../components/Teams/military/MilitaryEndReviewModal';
import MilitaryDetailedAnalyticsModal from '../../components/Teams/military/MilitaryDetailedAnalyticsModal';
import PendingSoldiersModal from '../../components/Teams/military/PendingSoldiersModal';
import { getTeamTypeDisplay } from '../../utils/teamTypeUtils';
import { useInterviewHandlers } from './hooks/useInterviewHandlers';
import { useMilitarySessionHandlers } from './hooks/useMilitarySessionHandlers';
import { useTeamOperations } from './hooks/useTeamOperations';
import useEndInterview from './hooks/useEndInterview';
import MilitarySessionReportModal from '../../components/Teams/military/MilitarySessionReportModal';

import config from '../../config';
import './Teams.css';
import { toast } from '../../components/Common/Toast';

const Teams = () => {
  const { userInfo: user, token } = useSelector(state => state.user);
  const currentUserId = user?.id || user?.user_id;

  // ─── Core State ───
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // ─── Interview State ───
  const [isCheckingInterview, setIsCheckingInterview] = useState(false);
  const [hasCheckedInitialInterview, setHasCheckedInitialInterview] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [activeInterview, setActiveInterview] = useState(null);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [interviewStartTime, setInterviewStartTime] = useState(null);
  const [showCandidateQuestions, setShowCandidateQuestions] = useState(false);
  const [currentInterviewForCandidate, setCurrentInterviewForCandidate] = useState(null);
  const [pendingInterview, setPendingInterview] = useState(null);
  const [showInterviewConfirm, setShowInterviewConfirm] = useState(false);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [nextQuestionTime, setNextQuestionTime] = useState(null);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [candidateProgress, setCandidateProgress] = useState(null);
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [completedRounds, setCompletedRounds] = useState([]);
  const [totalRounds] = useState(5);

  const [showGroupVideoCall, setShowGroupVideoCall] = useState(false);
  const [groupInterviewId, setGroupInterviewId] = useState(null);
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [groupCandidates, setGroupCandidates] = useState([]);
  const [isGroupCallMinimized, setIsGroupCallMinimized] = useState(false);

  const [videoCallSelectedUser, setVideoCallSelectedUser] = useState(null);

  // ─── Military State ───
  const [showMilitaryVideoCall, setShowMilitaryVideoCall] = useState(false);
  const [militarySessionId, setMilitarySessionId] = useState(null);
  const [activeMilitarySession, setActiveMilitarySession] = useState(null);
  const [isMilitaryCallMinimized, setIsMilitaryCallMinimized] = useState(false);
  const [pendingMilitarySession, setPendingMilitarySession] = useState(null);
  const [showMilitarySessionConfirm, setShowMilitarySessionConfirm] = useState(false);
  const [hasCheckedInitialMilitarySession, setHasCheckedInitialMilitarySession] = useState(false);
  const [showBatchManager, setShowBatchManager] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [currentBatchData, setCurrentBatchData] = useState(null);
  const [activeBatchSessions, setActiveBatchSessions] = useState([]);

  // ─── Modal State ───
  const [showTeamFormModal, setShowTeamFormModal] = useState(false);
  const [showAddMemberOptions, setShowAddMemberOptions] = useState(false);
  const [showMemberDetails, setShowMemberDetails] = useState(null);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [showAdminQuestions, setShowAdminQuestions] = useState(false);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsInterviewId, setAnalyticsInterviewId] = useState(null);
  const [analyticsCandidateId, setAnalyticsCandidateId] = useState(null);
  const [showPendingCandidates, setShowPendingCandidates] = useState(false);
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [loadingPendingCandidates, setLoadingPendingCandidates] = useState(false);
  const [pendingCandidatesCount, setPendingCandidatesCount] = useState(0);
  const [showMilitaryEndReviewModal, setShowMilitaryEndReviewModal] = useState(false);
  const [showMilitaryAnalyticsModal, setShowMilitaryAnalyticsModal] = useState(false);
  const [militaryAnalyticsSessionId, setMilitaryAnalyticsSessionId] = useState(null);
  const [militaryAnalyticsSoldierId, setMilitaryAnalyticsSoldierId] = useState(null);
  const [isMilitaryAnalytics, setIsMilitaryAnalytics] = useState(false);
  const [showPendingSoldiers, setShowPendingSoldiers] = useState(false);
  const [pendingSoldiers, setPendingSoldiers] = useState([]);
  const [loadingPendingSoldiers, setLoadingPendingSoldiers] = useState(false);
  const [pendingSoldiersCount, setPendingSoldiersCount] = useState(0);
  const [showMilitaryReportModal, setShowMilitaryReportModal] = useState(false);
  const [militaryReportSoldierId, setMilitaryReportSoldierId] = useState(null);
  const [militaryReportMember, setMilitaryReportMember] = useState(null);

  // ─── Refs ───
  const timers = useRef({});
  const askedQuestionsRef = useRef([]);
  const mountedRef = useRef(true);
  const activeInterviewIdRef = useRef(null);
  const isInterviewEndedByAdminRef = useRef(false);
  const batchCheckDoneRef = useRef(false);

  let fetchTeamMembers = null;

  const clearInterviewTimers = () => {
    if (timers.current.first) clearTimeout(timers.current.first);
    if (timers.current.interval) clearInterval(timers.current.interval);
    if (timers.current.candidate) clearTimeout(timers.current.candidate);
    if (timers.current.candidateInterval) clearInterval(timers.current.candidateInterval);
  };

  const handleStartMilitaryProgram = (programData) => {
    console.log('Military program started:', programData);

    if (programData.isGroupInterview && programData.candidates && programData.candidates.length > 0) {
      // Extract the shared batch/assessment ID
      const batchId = programData.assessment?.batch_id || programData.batchId;

      // Map candidates with their info
      const candidates = programData.candidates.map((c, index) => ({
        userId: c.user_id,
        displayName: c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : (c.candidate_name || `Soldier ${c.user_id}`),
        soldierId: programData.soldierIds?.[index] || c.soldier_id || null,
        sessionId: programData.sessions?.[index]?.session_id || null
      }));

      // USE BATCH ID AS THE SHARED ROOM ID
      const sharedSessionId = batchId;

      setMilitarySessionId(sharedSessionId);
      setCurrentBatchId(sharedSessionId);
      setCurrentBatchData({
        batchId: sharedSessionId,
        assessmentId: programData.assessment?.assessment_id,
        candidates: candidates,
        soldierIds: candidates.map(c => c.soldierId),
        questions: programData.assessment?.questions || programData.questions || [],
      });

      setActiveMilitarySession({
        session_id: sharedSessionId,
        week_number: 1,
        is_batch: true,
        assessment_id: programData.assessment?.assessment_id
      });

      setVideoCallSelectedUser({
        first_name: 'Military',
        last_name: 'Assessment',
        user_id: currentUserId,
      });

      setShowMilitaryVideoCall(true);
      setIsMilitaryCallMinimized(false);
      setShowAddMemberOptions(false);

      // toast.success(`Starting group assessment with ${candidates.length} soldier(s)`);

    } else if (programData.session) {
      // Single candidate
      setMilitarySessionId(programData.session.session_id);
      setActiveMilitarySession(programData.session);
      setVideoCallSelectedUser(programData.selectedFriend || {
        first_name: programData.candidate_name || 'Candidate',
        last_name: '',
        user_id: programData.user_id,
      });
      setShowMilitaryVideoCall(true);
      setIsMilitaryCallMinimized(false);
      setShowAddMemberOptions(false);
    }
  };

  const {
    loadAvailableQuestions,
    checkActiveInterview,
    checkIfUserInActiveInterview,
    checkPendingInterviewQuestions,
    logCandidateQuestion,
    askCandidateQuestion,
    handleStartInterview,
    handleEndInterview: endInterviewPerCandidate,
    getNextQuestion,
  } = useInterviewHandlers(
    token, user, selectedTeam, activeInterview, setActiveInterview, setIsInterviewActive,
    setInterviewQuestions, setCurrentQuestion, setCurrentQuestionIndex,
    setInterviewStartTime, setCandidateProgress, setCurrentInterviewForCandidate,
    setShowCandidateQuestions, setWaitingForNext, setNextQuestionTime, setInterviewCompleted,
    setPendingInterview, setShowInterviewConfirm, setShowAddMemberOptions, setShowInterviewModal,
    activeInterviewIdRef, isInterviewEndedByAdminRef, askedQuestionsRef,
    timers, availableQuestions, setAvailableQuestions, isAdmin,
    isCheckingInterview, setIsCheckingInterview, showCandidateQuestions, showInterviewConfirm,
    waitingForNext, interviewCompleted, isInterviewActive, interviewQuestions, currentQuestionIndex,
    currentQuestion, currentInterviewForCandidate, fetchTeamMembers, setIsStartingInterview,
    setLoading, isStartingInterview, setAskedQuestionIds, mountedRef, setCurrentRound,
    totalRounds, setCompletedRounds, completedRounds, setAnsweredQuestions, setTeams, setSelectedTeam
  );

  const {
    checkUserActiveMilitarySession,
    handleStartMilitarySession,
    handleStartMilitaryFromAddModal,
    handleJoinMilitarySession,
    handleMilitarySessionComplete: originalHandleMilitarySessionComplete,
    handleMilitarySessionClose: originalHandleMilitarySessionClose,
    abandonSession,
  } = useMilitarySessionHandlers(
    token, user, selectedTeam,
    setShowMilitaryVideoCall, setMilitarySessionId, setActiveMilitarySession,
    setIsMilitaryCallMinimized, setVideoCallSelectedUser,
    setPendingMilitarySession, setShowMilitarySessionConfirm,
    teamMembers, fetchTeamMembers, handleStartMilitaryProgram
  );

  const handleMilitarySessionComplete = useCallback(async (result) => {
    console.log("✅ Military session completed:", result);

    // Show the end review modal for admin
    if (isAdmin && currentBatchId) {
      setShowMilitaryEndReviewModal(true);
    }

    // Call the original handler
    await originalHandleMilitarySessionComplete(result);
  }, [isAdmin, currentBatchId, originalHandleMilitarySessionComplete]);

  const handleMilitarySessionClose = useCallback(() => {
    // Show review modal for admin when closing
    if (isAdmin && currentBatchId) {
      setShowMilitaryEndReviewModal(true);
    }

    // Call the original handler
    originalHandleMilitarySessionClose();
  }, [isAdmin, currentBatchId, originalHandleMilitarySessionClose]);

  const {
    fetchTeams, fetchFriends,
    fetchTeamMembers: fetchTeamMembersOp,
    handleCreateTeam, handleUpdateTeam, handleDeleteTeam,
    handleAddMember, handleRemoveMember, handleExitTeam,
  } = useTeamOperations(
    token, user, teams, setTeams, selectedTeam, setSelectedTeam,
    teamMembers, setTeamMembers, setShowTeamFormModal, teamToEdit, setTeamToEdit,
    setShowAddMemberOptions, setLoading, setFriends
  );

  fetchTeamMembers = fetchTeamMembersOp;

  const {
    handleEndInterview,
    showEndInterviewModal,
    setShowEndInterviewModal,
    handleCandidateAction,
    handleMeetingEndedCleanup,
    endInterviewId,
    isEnding
  } = useEndInterview(
    token, selectedTeam, fetchTeamMembers, setActiveInterview, setIsInterviewActive,
    setTeams, setSelectedTeam, setShowGroupVideoCall, setIsGroupCallMinimized,
    activeInterviewIdRef, clearInterviewTimers
  );

  const handleStartGroupInterview = async (interviewConfig) => {
    try {
      const result = await handleStartInterview(interviewConfig);
      if (result?.interview) {
        setGroupInterviewId(result.interview.interview_id);
        setGroupQuestions(result.interview.questions || []);
        setGroupCandidates(result.interview.candidates || []);
        setShowGroupVideoCall(true);
        setIsGroupCallMinimized(false);
      }
    } catch (err) {
      console.error('Failed to start group interview:', err);
      toast.error('Failed to start interview');
    }
  };

  const fetchPendingSoldiersCount = useCallback(async () => {
    if (!selectedTeam?.team_id || !isAdmin) return;
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/pending-soldiers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) setPendingSoldiersCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching pending soldiers count:', error);
    }
  }, [selectedTeam?.team_id, token, isAdmin]);

  const fetchPendingSoldiers = useCallback(async () => {
    if (!selectedTeam?.team_id) return;
    setLoadingPendingSoldiers(true);
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/pending-soldiers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setPendingSoldiers(data.soldiers || []);
        setPendingSoldiersCount(data.count || 0);
      }
    } catch (error) {
      toast.error('Failed to fetch pending soldiers');
    } finally {
      setLoadingPendingSoldiers(false);
    }
  }, [selectedTeam?.team_id, token]);

  const handleAcceptInterview = (interview) => {
    activeInterviewIdRef.current = interview.interview_id;
    setShowInterviewConfirm(false);
    setCurrentInterviewForCandidate({
      interviewId: interview.interview_id,
      teamName: interview.team_name,
      role: interview.role || 'candidate',
      candidateId: interview.candidateId,
      questions: interview.questions || [],
    });
    setShowCandidateQuestions(false);
    setWaitingForNext(false);
    setInterviewCompleted(false);
    setPendingInterview(null);
    isInterviewEndedByAdminRef.current = false;
    setGroupInterviewId(interview.interview_id);
    setGroupQuestions(interview.questions || []);
    setGroupCandidates(interview.candidates || []);
    setShowGroupVideoCall(true);
    setIsGroupCallMinimized(false);
  };

  const handleJoinMilitarySessionWrapper = () => {
    if (!pendingMilitarySession) return;

    const session = pendingMilitarySession;

    console.log('Joining military session with FULL data:', session);

    // Use batch_id as session ID for shared room
    const sharedSessionId = session.session_id; // This is the batch ID
    const individualSessionId = session.individual_session_id || session.session_id;

    // ✅ FIXED: Map candidates properly with their session data
    let candidates = session.candidates || [];

    // If candidates array is empty/null, create a single candidate entry from the session data
    if (candidates.length === 0) {
      candidates = [{
        userId: user?.id || currentUserId,
        soldierId: session.soldier_id,
        displayName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || `Soldier ${currentUserId}`,
        sessionId: individualSessionId
      }];
    }

    // ✅ FIXED: Extract soldierIds properly
    const soldierIds = candidates.map(c => c.soldierId || c.soldier_id || null);

    console.log('🔍 Mapped candidates:', candidates);
    console.log('🔍 Extracted soldierIds:', soldierIds);

    setMilitarySessionId(sharedSessionId);
    setCurrentBatchId(sharedSessionId);
    setCurrentBatchData({
      batchId: sharedSessionId,
      candidates: candidates,
      soldierIds: soldierIds,
      questions: session.questions || [],
      individualSessionId: individualSessionId,
      assessmentId: session.assessment_id
    });

    setActiveMilitarySession({
      session_id: sharedSessionId,
      individual_session_id: individualSessionId,
      program_id: session.program_id,
      assessment_id: session.assessment_id,
      week_number: session.week_number || 1,
      soldier_id: soldierIds[0], // ✅ FIXED: Store soldier_id
      is_batch: session.is_batch || candidates.length > 1,
      questions: session.questions || []
    });

    setVideoCallSelectedUser({
      first_name: 'Military',
      last_name: 'Assessment',
      user_id: currentUserId,
    });

    setShowMilitaryVideoCall(true);
    setIsMilitaryCallMinimized(false);
    setShowMilitarySessionConfirm(false);
    setPendingMilitarySession(null);
  };

  const handleSelectTeam = (team) => {
    setActiveInterview(null);
    setIsInterviewActive(false);
    setInterviewQuestions([]);
    setCurrentQuestionIndex(0);
    setCurrentQuestion(null);
    setInterviewStartTime(null);
    setCandidateProgress(null);
    setShowMilitaryVideoCall(false);
    setMilitarySessionId(null);
    setActiveMilitarySession(null);
    setShowBatchManager(false);
    setCurrentBatchId(null);
    setCurrentBatchData(null);
    setShowGroupVideoCall(false);
    setGroupInterviewId(null);
    setGroupCandidates([]);
    setGroupQuestions([]);
    setVideoCallSelectedUser(null);
    isInterviewEndedByAdminRef.current = false;
    activeInterviewIdRef.current = null;
    batchCheckDoneRef.current = false;
    clearInterviewTimers();
    setSelectedTeam(team);
    setIsAdmin(String(team.created_by) === String(currentUserId));
    fetchTeamMembersOp(team.team_id);
  };

  const fetchPendingCandidatesCount = useCallback(async () => {
    if (!selectedTeam?.team_id || !isAdmin) return;
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/pending-candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) setPendingCandidatesCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching pending candidates count:', error);
    }
  }, [selectedTeam?.team_id, token, isAdmin]);

  const fetchPendingCandidates = useCallback(async () => {
    if (!selectedTeam?.team_id) return;
    setLoadingPendingCandidates(true);
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/pending-candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setPendingCandidates(data.candidates || []);
        setPendingCandidatesCount(data.count || 0);
      }
    } catch (error) {
      toast.error('Failed to fetch pending candidates');
    } finally {
      setLoadingPendingCandidates(false);
    }
  }, [selectedTeam?.team_id, token]);

  const handleStartInterviewWithPending = useCallback((candidate) => {
    setShowPendingCandidates(false);
    handleStartGroupInterview({
      mode: candidate.interview_mode || 'group',
      capture: [],
      candidates: [candidate.candidate_id]
    });
  }, [handleStartGroupInterview]);

  // ─── Effects ───
  useEffect(() => {
    const handler = ({ detail }) => {
      // Check if this is a military analytics request
      if (detail.batchId || detail.isMilitary) {
        setMilitaryAnalyticsSessionId(detail.interviewId); // This is the session_id
        setMilitaryAnalyticsSoldierId(detail.candidateId); // This is the soldier_id
        setIsMilitaryAnalytics(true);
        setShowMilitaryAnalyticsModal(true);
      } else {
        // Regular interview analytics
        setAnalyticsInterviewId(detail.interviewId);
        setAnalyticsCandidateId(detail.candidateId || null);
        setShowAnalyticsModal(true);
      }
    };
    window.addEventListener('openAnalyticsModal', handler);
    return () => window.removeEventListener('openAnalyticsModal', handler);
  }, []);

  // ✅ FIXED: Socket connection with military assessment invitation
  useEffect(() => {
    if (!token || !currentUserId) return;
    const socket = io(config.baseURL, { auth: { token } });

    socket.on("connect_error", (err) => { console.log("Socket error:", err.message); });

    // Interview invitations
    socket.on('interview-invitation', (interview) => {
      if (activeInterviewIdRef.current || isInterviewActive || showGroupVideoCall) return;
      if (!interview?.interview_id) return;
      setPendingInterview(interview);
      setShowInterviewConfirm(true);
    });

    // ✅ NEW: Military assessment invitations
    socket.on('military-assessment-invitation', (assessment) => {
      console.log('📡 Received military assessment invitation:', assessment);

      // Don't show if already in a session
      if (showMilitaryVideoCall || showGroupVideoCall || isInterviewActive) {
        console.log('Already in a session, ignoring invitation');
        return;
      }

      if (!assessment?.batch_id) return;
      checkUserActiveMilitarySession();
    });

    socket.on('interview-ended', (data) => {
      const { interviewId, decision } = data;
      const isCurrent = String(groupInterviewId) === String(interviewId) ||
        String(activeInterviewIdRef.current) === String(interviewId);
      if (!isCurrent) return;
      setShowGroupVideoCall(false);
      setIsGroupCallMinimized(false);
      setGroupInterviewId(null);
      setGroupQuestions([]);
      setGroupCandidates([]);
      setActiveInterview(null);
      setIsInterviewActive(false);
      setCurrentInterviewForCandidate(null);
      setShowCandidateQuestions(false);
      setWaitingForNext(false);
      setInterviewCompleted(false);
      setPendingInterview(null);
      setShowInterviewConfirm(false);
      activeInterviewIdRef.current = null;
      isInterviewEndedByAdminRef.current = false;
      clearInterviewTimers();
      if (selectedTeam?.team_id && fetchTeamMembers) fetchTeamMembers(selectedTeam.team_id);
      fetchTeams();
      if (decision === 'selected') {
        toast.success('Interview completed');
      } else {
        toast.info('Interview has ended');
      }
    });

    socket.on('candidate-interview-ended', ({ teamMemberAdded }) => {
      if (teamMemberAdded) {
        toast.success('Candidate has been added to the team!');
        if (selectedTeam?.team_id) fetchTeamMembers(selectedTeam.team_id);
      }
    });

    return () => {
      socket.off('connect');
      socket.off('interview-invitation');
      socket.off('military-assessment-invitation');
      socket.off('interview-ended');
      socket.off('candidate-interview-ended');
      socket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (currentUserId) {
      fetchTeams();
      fetchFriends();
      loadAvailableQuestions();
    }
  }, [currentUserId]);

  useEffect(() => {
    const checkActiveInterviewStatus = async () => {
      if (!selectedTeam?.team_id) return;
      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/teams/${selectedTeam.team_id}/active-interview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data?.success) {
          if (data.active_interview) {
            setActiveInterview(data.active_interview);
            setIsInterviewActive(true);
            activeInterviewIdRef.current = data.active_interview.interview_id;
            setSelectedTeam(prev => prev ? { ...prev, active_interview: data.active_interview } : prev);
          } else {
            setActiveInterview(null);
            setIsInterviewActive(false);
            activeInterviewIdRef.current = null;
            setSelectedTeam(prev => prev ? { ...prev, active_interview: null } : prev);
          }
        }
      } catch (err) {
        console.error('Error checking active interview:', err);
      }
    };
    if (selectedTeam && token) checkActiveInterviewStatus();
  }, [selectedTeam?.team_id, token]);

  useEffect(() => {
    if (selectedTeam) setIsAdmin(String(selectedTeam.created_by) === String(currentUserId));
  }, [selectedTeam, currentUserId]);

  useEffect(() => {
    if (!currentUserId || !token || hasCheckedInitialInterview) return;
    const runInitialChecks = async () => {
      setHasCheckedInitialInterview(true);
      let hasRegularInterview = false;
      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/teams/user/active-interview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data?.success && data?.active_interview && !showInterviewConfirm && !interviewCompleted && !currentInterviewForCandidate) {
          hasRegularInterview = true;
          setPendingInterview(data.active_interview);
          setShowInterviewConfirm(true);
          setIsAdmin(String(data?.active_interview?.admin_id) === String(currentUserId));
        }
      } catch (err) {
        console.error('Error checking active interview on mount:', err);
      }
      await checkUserActiveMilitarySession({ skipIfInterviewPending: hasRegularInterview });
    };
    runInitialChecks();
  }, [currentUserId, token]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearInterviewTimers(); };
  }, []);

  useEffect(() => {
    const handler = async (event) => {
      if (selectedTeam?.team_id === event.detail.teamId && fetchTeamMembersOp) {
        await fetchTeamMembersOp(event.detail.teamId);
      }
    };
    window.addEventListener('refreshTeamMembers', handler);
    return () => window.removeEventListener('refreshTeamMembers', handler);
  }, [selectedTeam?.team_id, fetchTeamMembersOp]);

  useEffect(() => {
    if (selectedTeam) {
      batchCheckDoneRef.current = false;
      fetchPendingCandidatesCount();
      fetchPendingSoldiersCount();
    }
  }, [selectedTeam, fetchPendingCandidatesCount, fetchPendingSoldiersCount, showMilitaryEndReviewModal, showEndInterviewModal]);

  const handleStartMilitaryForPendingSoldier = useCallback(async (soldier) => {
    setShowPendingSoldiers(false);
    setIsStartingInterview(true);

    try {
      // Always send candidates as an array
      const response = await axios.post(
        `${config.baseURL}/api/military/teams/${selectedTeam.team_id}/start-program`,
        {
          config: {
            candidates: [
              {
                user_id: soldier.user_id,
                first_name: soldier.first_name || '',
                last_name: soldier.last_name || '',
                candidate_name: `${soldier.first_name || ''} ${soldier.last_name || ''}`.trim()
              }
            ],
            // Include any additional config
            batch_id: `batch_${Date.now()}_${soldier.user_id}`
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Military assessment started!');

        const { assessment } = response.data;

        // The assessment response will have the session info
        if (assessment.sessions && assessment.sessions.length > 0) {
          const firstSession = assessment.sessions[0];

          setActiveMilitarySession({
            session_id: assessment.batch_id, // Use batch_id as the shared session
            individual_session_id: firstSession.session_id,
            assessment_id: assessment.assessment_id,
            week_number: 1,
            is_batch: true,
            questions: assessment.questions || []
          });

          setMilitarySessionId(assessment.batch_id);
          setCurrentBatchId(assessment.batch_id);
          setCurrentBatchData({
            batchId: assessment.batch_id,
            candidates: assessment.candidates,
            soldierIds: assessment.candidates.map(c => c.soldier_id),
            questions: assessment.questions || [],
            assessmentId: assessment.assessment_id
          });

          setShowMilitaryVideoCall(true);
          setIsMilitaryCallMinimized(false);

          setVideoCallSelectedUser({
            first_name: soldier.first_name || 'Candidate',
            last_name: soldier.last_name || '',
            user_id: soldier.user_id
          });
        }

        // Refresh data
        if (selectedTeam?.team_id) {
          fetchTeamMembers(selectedTeam.team_id);
          fetchPendingSoldiersCount();
        }

        return { success: true };
      }

      throw new Error(response.data.message || 'Failed to start assessment');
    } catch (error) {
      console.error('Error starting assessment for pending soldier:', error);
      toast.error(error.response?.data?.message || 'Failed to start assessment');
    } finally {
      setIsStartingInterview(false);
    }
  }, [selectedTeam, token, fetchTeamMembers, fetchPendingSoldiersCount,
    setActiveMilitarySession, setMilitarySessionId, setShowMilitaryVideoCall,
    setIsMilitaryCallMinimized, setVideoCallSelectedUser]);

  const handleStartMilitaryFromPending = useCallback((soldier) => {
    handleStartMilitaryForPendingSoldier(soldier);
  }, [handleStartMilitaryForPendingSoldier]);
  const handleStartNextMilitarySession = useCallback(async (member) => {
    if (!selectedTeam?.team_id) return;

    const soldierId = member.soldier_id || member.soldierId;

    if (!soldierId) {
      toast.error('Soldier ID not found');
      return;
    }

    setIsStartingInterview(true);

    try {
      const { data } = await axios.post(
        `${config.baseURL}/api/military/soldiers/${soldierId}/next-session`,
        { team_id: selectedTeam.team_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data.success) {
        toast.error(data.message || 'Failed to start next interview');
        return;
      }

      const session = data.session;
      const displayName = `${member.first_name || ''} ${member.last_name || ''}`.trim();

      setMilitarySessionId(session.session_id);
      setCurrentBatchId(null);

      setCurrentBatchData({
        batchId: null,
        individualSessionId: session.session_id,
        candidates: [
          {
            userId: member.user_id,
            soldierId,
            sessionId: session.session_id,
            displayName: displayName || `Soldier ${member.user_id}`
          }
        ],
        soldierIds: [soldierId],
        questions: session.questions || []
      });

      setActiveMilitarySession({
        session_id: session.session_id,
        individual_session_id: session.session_id,
        program_id: session.program_id,
        soldier_id: soldierId,
        week_number: session.week_number,
        is_batch: false,
        questions: session.questions || []
      });

      setVideoCallSelectedUser({
        first_name: member.first_name || 'Candidate',
        last_name: member.last_name || '',
        user_id: member.user_id
      });

      setShowMilitaryVideoCall(true);
      setIsMilitaryCallMinimized(false);

      toast.success(`Week ${session.week_number} interview started`);

      if (selectedTeam?.team_id) {
        fetchTeamMembersOp(selectedTeam.team_id);
      }
    } catch (error) {
      console.error('Start next military session error:', error);
      toast.error(error.response?.data?.message || 'Failed to start next interview');
    } finally {
      setIsStartingInterview(false);
    }
  }, [selectedTeam?.team_id, token, fetchTeamMembersOp]);

  const handleViewMilitaryReport = useCallback(async (member) => {
    let soldierId = member.soldier_id || member.soldierId;

    try {
      if (!soldierId && member.user_id) {
        const { data } = await axios.get(
          `${config.baseURL}/api/military/user/${member.user_id}/program-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (data.success && data.program) {
          soldierId = data.program.soldier_id || data.program.soldierId;
        }
      }

      if (!soldierId) {
        toast.error('Soldier ID not found for this member');
        return;
      }

      setShowMilitaryAnalyticsModal(false);
      setMilitaryAnalyticsSessionId(null);
      setMilitaryAnalyticsSoldierId(null);
      setIsMilitaryAnalytics(false);

      setShowAnalyticsModal(false);
      setAnalyticsInterviewId(null);
      setAnalyticsCandidateId(null);

      setMilitaryReportSoldierId(soldierId);
      setMilitaryReportMember(member);
      setShowMilitaryReportModal(true);
    } catch (error) {
      console.error('Fetch soldier ID error:', error);
      toast.error('Failed to load soldier report');
    }
  }, [token]);


  // ─── Computed Values ───
  const availableFriends = friends.filter(
    f => !teamMembers.some(m => String(m.user_id) === String(f.user_id))
  );

  const filteredTeamMembers = teamMembers.filter(member => {
    if (!memberSearchQuery.trim()) return true;
    const q = memberSearchQuery.toLowerCase().trim();
    const firstName = (member.first_name || '').toLowerCase();
    const lastName = (member.last_name || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`.trim();
    return ['email', 'username', 'profileId'].some(field => {
      return (member[field] || '').toLowerCase().includes(q);
    }) || firstName.includes(q) || lastName.includes(q) || fullName.includes(q);
  });

  if (!user) {
    return (
      <div className="teams-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="teams-page">
      <div className="teams-header">
        <h1 className="teams-title">Teams Management</h1>
        <p className="teams-subtitle">Create teams and manage members from your friends list</p>
      </div>

      <div className="teams-layout">
        <div className="teams-left-panel">
          <TeamsList
            teams={teams}
            selectedTeam={selectedTeam}
            onSelectTeam={handleSelectTeam}
            onEditTeam={(team) => { setTeamToEdit(team); setFormMode('edit'); setShowTeamFormModal(true); }}
            onDeleteTeam={handleDeleteTeam}
            onCreateTeam={() => { setTeamToEdit(null); setFormMode('create'); setShowTeamFormModal(true); }}
            currentUserId={currentUserId}
            onExitTeam={handleExitTeam}
          />
        </div>

        <div className="teams-right-panel">
          {selectedTeam ? (
            <>
              <div className="team-details-header">
                <div className="team-title-section">
                  <div className="team-title-row">
                    <h2 className="team-details-title">{selectedTeam.team_name}</h2>
                    {getTeamTypeDisplay(selectedTeam) && (
                      <span className={`team-type-simple ${getTeamTypeDisplay(selectedTeam).className}`}>
                        {getTeamTypeDisplay(selectedTeam).fullDisplay}
                      </span>
                    )}
                  </div>
                  {selectedTeam.description && (
                    <p className="team-details-description">{selectedTeam.description}</p>
                  )}
                </div>

                <div className="team-actions-nav">
                  {isAdmin && (
                    <>
                      <button className="add-member-btn" onClick={() => setShowAddMemberOptions(true)} disabled={isInterviewActive}>
                        <FaUserPlus /> Add Members
                      </button>

                      {pendingCandidatesCount > 0 && (
                        <button className="pending-candidates-btn" onClick={() => { fetchPendingCandidates(); setShowPendingCandidates(true); }}>
                          <FaUserClock /> Pending ({pendingCandidatesCount})
                        </button>
                      )}

                      {pendingSoldiersCount > 0 && (
                        <button
                          className="pending-candidates-btn"
                          onClick={() => {
                            fetchPendingSoldiers();
                            setShowPendingSoldiers(true);
                          }}
                        >
                          <FaUserClock /> Pending Soldiers ({pendingSoldiersCount})
                        </button>
                      )}

                      {isInterviewActive && selectedTeam?.active_interview && (
                        <button className="manage-interview-btn" onClick={() => {
                          const interview = selectedTeam.active_interview;
                          setGroupInterviewId(interview.interview_id);
                          setActiveInterview(interview);
                          setGroupQuestions([]);
                          setGroupCandidates([]);
                          if (interview.interview_id) activeInterviewIdRef.current = interview.interview_id;
                          setShowGroupVideoCall(true);
                          setIsGroupCallMinimized(false);
                        }}>
                          Manage Interview
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="team-members-section">
                <div className="members-header">
                  <h3 className="members-title">Team Members ({teamMembers.length})</h3>
                  {teamMembers.length > 0 && (
                    <div className="member-search-container">
                      <input
                        type="text"
                        className="member-search-input"
                        placeholder="Name, ID and Email"
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                      />
                      {memberSearchQuery && (
                        <button className="member-search-clear" onClick={() => setMemberSearchQuery('')} type="button">✕</button>
                      )}
                    </div>
                  )}
                </div>

                {teamMembers.length === 0 ? (
                  <div className="empty-members">
                    <div className="empty-icon">👤</div>
                    <h3>No Members Yet</h3>
                    <p>Add members from your friends list</p>
                  </div>
                ) : filteredTeamMembers.length === 0 ? (
                  <div className="empty-members">
                    <div className="empty-icon">🔍</div>
                    <h3>No Members Found</h3>
                    <p>No members match "{memberSearchQuery}"</p>
                  </div>
                ) : (
                  <div className="members-grid">
                    {filteredTeamMembers.map(member => (
                      <MemberCard
                        key={member.team_member_id}
                        isAdmin={isAdmin}
                        user={user}
                        token={token}
                        member={member}

                        onRemove={handleRemoveMember}
                        onViewDetails={setShowMemberDetails}
                        currentUserId={currentUserId}
                        teamId={selectedTeam?.team_id}
                        onStartInterview={handleStartGroupInterview}
                        onStartMilitarySession={handleStartMilitarySession}
                        onStartNextMilitarySession={handleStartNextMilitarySession}
                        onViewMilitaryReport={handleViewMilitaryReport}
                        teamName={selectedTeam?.team_name}
                        teamType={selectedTeam?.team_type}
                        onEndInterview={handleEndInterview}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-team-selected">
              <div className="empty-state">
                <div className="empty-icon">🏢</div>
                <h3>Select a Team</h3>
                <p>Choose a team from the list to view members and details</p>
                <button className="create-team-btn-large" onClick={() => { setTeamToEdit(null); setFormMode('create'); setShowTeamFormModal(true); }}>
                  Create your team
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TeamFormModal
        show={showTeamFormModal}
        onClose={() => { setShowTeamFormModal(false); setTeamToEdit(null); }}
        onSubmit={formMode === 'edit' ? handleUpdateTeam : handleCreateTeam}
        loading={loading}
        teamToEdit={teamToEdit}
        mode={formMode}
      />

      <InterviewSetupModal
        show={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        onStart={handleStartGroupInterview}
        teamName={selectedTeam?.team_name}
        selectedFriends={[]}
      />

      <AddMemberOptionsModal
        show={showAddMemberOptions}
        onClose={() => setShowAddMemberOptions(false)}
        teamId={selectedTeam?.team_id}
        teamName={selectedTeam?.team_name}
        teamType={selectedTeam?.team_type}
        friends={availableFriends}
        teamMembers={teamMembers}
        onAddMember={handleAddMember}
        onStartInterview={handleStartGroupInterview}
        onStartMilitaryProgram={handleStartMilitaryFromAddModal}
        token={token}
        user={user}
      />

      {showMemberDetails && (
        <MemberDetailsModal
          member={showMemberDetails}
          onClose={() => setShowMemberDetails(null)}
          currentUserId={currentUserId}
        />
      )}

      {showInterviewConfirm && pendingInterview && !isAdmin && (
        <div className="interview-confirm-overlay">
          <div className="interview-confirm-modal">
            <h2>Interview Invitation</h2>
            <p>You have been invited for an interview with team <strong>{pendingInterview.team_name}</strong></p>
            <p>Total Questions: {pendingInterview.questions?.length || pendingInterview.total_questions || 25}</p>
            <p>Would you like to start now?</p>
            <div className="confirm-actions">
              <button className="decline-btn" onClick={() => { setShowInterviewConfirm(false); setPendingInterview(null); }}>Later</button>
              <button className="accept-btn" onClick={() => handleAcceptInterview(pendingInterview)}>Start Now</button>
            </div>
          </div>
        </div>
      )}

      {showMilitarySessionConfirm && pendingMilitarySession && (
        <div className="interview-confirm-overlay">
          <div className="interview-confirm-modal">
            <h2>Military Assessment</h2>
            <p>You have an active military assessment with team <strong>{pendingMilitarySession.team_name}</strong></p>
            <div className="military-session-details">
              <div className="detail-row"><span className="detail-label">Week:</span><span className="detail-value">{pendingMilitarySession.week_number} of 6</span></div>
              <div className="detail-row"><span className="detail-label">Progress:</span><span className="detail-value">{pendingMilitarySession.answered_count} / {pendingMilitarySession.total_questions} questions</span></div>
              <div className="progress-bar-container" style={{ marginTop: 12 }}>
                <div className="progress-bar-fill" style={{ width: `${(pendingMilitarySession.answered_count / pendingMilitarySession.total_questions) * 100}%`, background: 'linear-gradient(90deg,#27ae60,#2ecc71)' }} />
              </div>
            </div>
            <p>Would you like to continue your assessment?</p>
            <div className="confirm-actions">
              <button className="decline-btn" onClick={() => { setShowMilitarySessionConfirm(false); setPendingMilitarySession(null); }}>Later</button>
              <button className="accept-btn" onClick={handleJoinMilitarySessionWrapper} style={{ background: '#27ae60' }}>Continue Assessment</button>
            </div>
          </div>
        </div>
      )}

      {showGroupVideoCall && groupInterviewId && (
        <div className={`video-call-modal-overlay ${isGroupCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className={`video-call-modal-container ${isGroupCallMinimized ? 'minimized-container' : ''}`}>
            <InterviewVideoCall
              roomId={String(groupInterviewId)}
              userId={currentUserId}
              token={token}
              interviewId={groupInterviewId}
              user={user}
              isAdmin={isAdmin}
              role={isAdmin ? 'admin' : 'candidate'}
              candidateId={currentInterviewForCandidate?.candidateId || null}
              questions={groupQuestions}
              participants={groupCandidates}
              teamName={selectedTeam?.team_name || currentInterviewForCandidate?.teamName}
              isMinimized={isGroupCallMinimized}
              onToggleMinimize={() => setIsGroupCallMinimized(prev => !prev)}
              onMeetingEnd={handleMeetingEndedCleanup}
              teamId={selectedTeam?.team_id}
            />
          </div>
        </div>
      )}

      {showMilitaryVideoCall && (militarySessionId || currentBatchId) && !showBatchManager && (
        <div className={`video-call-modal-overlay ${isMilitaryCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className={`video-call-modal-container ${isMilitaryCallMinimized ? 'minimized-container' : ''}`}>
            <MilitarySessionVideoCall
              sessionId={militarySessionId || currentBatchId}
              individualSessionId={currentBatchData?.individualSessionId || militarySessionId}
              programId={activeMilitarySession?.program_id}
              weekNumber={activeMilitarySession?.week_number || 1}
              userId={currentUserId}
              token={token}
              user={user}
              selectedUser={videoCallSelectedUser}
              isAdmin={isAdmin}
              teamId={selectedTeam?.team_id}
              teamName={selectedTeam?.team_name || pendingMilitarySession?.team_name}
              onSessionComplete={handleMilitarySessionComplete}
              onClose={handleMilitarySessionClose}
              onAbandonSession={abandonSession}
              isMinimized={isMilitaryCallMinimized}
              onToggleMinimize={() => setIsMilitaryCallMinimized(prev => !prev)}
              isBatchSession={!!currentBatchId}
              batchId={currentBatchId}
              batchData={currentBatchData}
              groupCandidates={currentBatchData?.candidates || []}
              soldierId={isAdmin ? null : (
                currentBatchData?.soldierIds?.[0] ||
                pendingMilitarySession?.soldier_id ||
                activeMilitarySession?.soldier_id ||
                currentBatchData?.candidates?.find(c => String(c.userId) === String(currentUserId))?.soldierId ||
                null
              )}
            />
          </div>
        </div>
      )}

      {showAnalyticsModal && analyticsInterviewId && isAdmin && (
        <DetailedAnalyticsModal
          interviewId={analyticsInterviewId}
          token={token}
          candidateId={analyticsCandidateId}
          onClose={() => { setShowAnalyticsModal(false); setAnalyticsInterviewId(null); setAnalyticsCandidateId(null); }}
        />
      )}

      {/* Add this new block for military analytics */}
      {showMilitaryAnalyticsModal && militaryAnalyticsSessionId && isAdmin && (
        <MilitaryDetailedAnalyticsModal
          interviewId={militaryAnalyticsSessionId}
          token={token}
          candidateId={militaryAnalyticsSoldierId}
          onClose={() => {
            setShowMilitaryAnalyticsModal(false);
            setMilitaryAnalyticsSessionId(null);
            setMilitaryAnalyticsSoldierId(null);
            setIsMilitaryAnalytics(false);
          }}
        />
      )}

      {showEndInterviewModal && isAdmin && (
        <EndInterviewModal
          show={showEndInterviewModal}
          onClose={() => setShowEndInterviewModal(false)}
          interviewId={endInterviewId || selectedTeam?.active_interview?.interview_id || activeInterviewIdRef.current}
          teamId={selectedTeam?.team_id}
          token={token}
          onCandidateAction={handleCandidateAction}
        />
      )}

      {showPendingCandidates && (
        <PendingCandidatesModal
          show={showPendingCandidates}
          onClose={() => setShowPendingCandidates(false)}
          candidates={pendingCandidates}
          onStartInterview={handleStartInterviewWithPending}
          loading={loadingPendingCandidates}
        />
      )}

      {showMilitaryEndReviewModal && isAdmin && (
        <MilitaryEndReviewModal
          show={showMilitaryEndReviewModal}
          onClose={() => {
            setShowMilitaryEndReviewModal(false);
            // Fetch team members after closing the modal
            if (selectedTeam?.team_id) {
              fetchTeamMembersOp(selectedTeam.team_id);
            }
          }}
          batchId={currentBatchId}
          teamId={selectedTeam?.team_id}
          token={token}
        />
      )}

      {showPendingSoldiers && (
        <PendingSoldiersModal
          show={showPendingSoldiers}
          onClose={() => setShowPendingSoldiers(false)}
          soldiers={pendingSoldiers}
          onStartAssessment={handleStartMilitaryFromPending} // This now uses the correct handler
          loading={loadingPendingSoldiers}
        />
      )}
      {showMilitaryReportModal && militaryReportSoldierId && (
        <MilitarySessionReportModal
          show={showMilitaryReportModal}
          soldierId={militaryReportSoldierId}
          member={militaryReportMember}
          token={token}
          onClose={() => {
            setShowMilitaryReportModal(false);
            setMilitaryReportSoldierId(null);
            setMilitaryReportMember(null);
          }}
        />
      )}
    </div>
  );
};

export default Teams;